import { useQuantitativeDataSummaryStats } from 'features/dataLayers'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useFfsComponentCdfs } from 'features/libra/stepComponents/ffs/useFfsComponentCdfs'

interface UseObservedMinThicknessArgs {
  componentId: string | undefined
  inspectionId: string | undefined
}

export interface UseObservedMinThicknessResult {
  /** Minimum measurement in mm, or null if the CDF isn't loaded yet. */
  observedMinMm: number | null
  isLoading: boolean
  /** True when no thickness CDF was found -- check cannot run. */
  noCdf: boolean
}

/**
 * Returns the observed minimum thickness from the inspection's thickness CDF.
 *
 * Delegates to `useQuantitativeDataSummaryStats` -- the same hook that powers
 * the asset-viz pane's Lo/Hi readout. Computation runs in a web worker; the
 * result is SWR-cached by `(cdfId, generation)` and shared across every
 * consumer in the workspace. We just project `value_range.min` into mm.
 */
export function useObservedMinThickness({
  componentId,
  inspectionId,
}: UseObservedMinThicknessArgs): UseObservedMinThicknessResult {
  const { thicknessCdf, isLoading: cdfLoading } = useFfsComponentCdfs({
    componentId,
    inspectionId,
  })
  const { data: stats, isLoading: statsLoading } =
    useQuantitativeDataSummaryStats(thicknessCdf)

  const observedMinMm =
    stats?.value_range?.min?.convertTo(WORKING_UNIT_THICKNESS).num ?? null

  return {
    observedMinMm,
    isLoading: cdfLoading || statsLoading,
    noCdf: !cdfLoading && !thicknessCdf,
  }
}
